module EEX.McEc.Commands {

    import CertificateModel = McEc.Domain.CertificateModel;
    import CertificateState = McEc.Domain.CertificateState;
    import CertificateAttributes = McEc.Controls.CertificateDialog.CertificateAttributes;

    export enum RequestCertificateState {
        Initial,
        CsrUploaded,
        CertificateRequested,
        CertificateDownloaded,
        ErrorCsrUploaded,
        ErrorCertificateRequested,
        ErrorCertificateDownloaded,
        Timeout,
        Success
    }

    export class RequestCertificateCommand {

        private authorityId: string;
        private isServerCertificate: boolean;
        private stateChanged: (state: RequestCertificateState) => void;
        private state: RequestCertificateState;

        private refreshCycle: number = 5000;
        private timeoutTime: number = 60000;

        private certificateKey: number;
        private certificateFile: string;

        public getCertificateKey(): number {
            return this.certificateKey;
        }

        public getCertificateFile(): string {
            return this.certificateFile;
        }

        constructor(authorityId: string, isServerCertificate: boolean) {
            this.authorityId = authorityId;
            this.isServerCertificate = isServerCertificate;
        }

        public request(store: Pki.Store, certificateAttributes: CertificateAttributes, isServerCertificate: boolean,
            stateChanged?: (state: RequestCertificateState) => void
        ) {
            this.stateChanged = stateChanged;

            this.createCsr(store, certificateAttributes, isServerCertificate,
                (csr) => {
                    this.receivedCsr(csr, certificateAttributes);
                });

            setTimeout(() => {
                if (this.state !== RequestCertificateState.Success
                    && this.state !== RequestCertificateState.ErrorCsrUploaded
                    && this.state !== RequestCertificateState.ErrorCertificateRequested
                    && this.state !== RequestCertificateState.ErrorCertificateDownloaded) {
                    this.changeState(RequestCertificateState.Timeout);
                }
            }, this.timeoutTime);
        }

        private receivedCsr(csr: any, certificateAttributes: CertificateAttributes) {
            new McEc.Data.Provider.AddCsrProvider().add({
                    FileContent: csr,
                    AuthorityId: this.authorityId,
                    IsServerCertificate: this.isServerCertificate,
                    Subject: this.getSubjectString(certificateAttributes)
                })
                .then(certificate => this.csrAdded(certificate))
                .fail(message => {
                    this.changeState(RequestCertificateState.ErrorCsrUploaded);
                    AppKitchen.UIHelper.Notifications.notify(message, "warning");
                });
        }

        private csrAdded(certificate: CertificateModel) {
            if (certificate) {
                this.changeState(RequestCertificateState.CsrUploaded);
                this.loadAndCheckCertificateSubmission(certificate.Key);
            } else {
                this.changeState(RequestCertificateState.ErrorCsrUploaded);
            }
        }

        private changeState(state: RequestCertificateState) {
            this.state = state;
            if (this.stateChanged) {
                this.stateChanged(this.state);
            }
        }

        private getSubjectString(c: CertificateAttributes): string {
            let subjects: string[] = [];
            this.addIfSet(subjects, "C", c.countryName);
            this.addIfSet(subjects, "L", c.localityName);
            this.addIfSet(subjects, "ST", c.stateName);
            this.addIfSet(subjects, "O", c.organizationName);
            this.addIfSet(subjects, "OU", c.organizationalUnitName);
            this.addIfSet(subjects, "CN", c.commonName);
            return subjects.join(",");
        }

        private addIfSet(subjects: string[], key: string, value: string) {
            if (value) {
                subjects.push(`${key}=${value}`);
            }
        }

        private createCsr(store: Pki.Store, certificateAttributes: CertificateAttributes, isServerCertificate: boolean, success: (csr: string | Pki.Error) => void) {
            var requestResult = store.createCertificateRequest(certificateAttributes, isServerCertificate);
            if (requestResult instanceof Pki.Error) {
                AppKitchen.UIHelper.Notifications.notify(requestResult.msg, "warning", { closeAfter: 3000 });
            }
            let csr = requestResult;
            success(csr);
        }

        private loadAndCheckCertificateSubmission(certificateKey: number) {
            new McEc.Data.Provider.CertificateProvider().load({ CertificateKey: certificateKey })
                .then(certificate => this.certificateLoaded(certificateKey, certificate))
                .fail(message => {
                    this.changeState(RequestCertificateState.ErrorCertificateRequested);
                    AppKitchen.UIHelper.Notifications.notify(message, "warning");
                });
        }

        private certificateLoaded(certificateKey: number, certificate: CertificateModel) {
            if (certificate) {
                this.checkCertificateSubmission(certificateKey, certificate.State);
            } else {
                this.waitForRefreshCycle(certificateKey);
            }
        }

        private checkCertificateSubmission(certificateKey: number, state: CertificateState) {
            switch (state) {
                case Domain.CertificateState.Issued:
                    this.changeState(RequestCertificateState.CertificateRequested);
                    this.downloadCertificateFile(certificateKey);
                    break;
                case Domain.CertificateState.Delayed:
                    AppKitchen.UIHelper.Notifications.notify("Delayed", "info", { closeAfter: 3000 });
                    break;
                case Domain.CertificateState.Created:
                case Domain.CertificateState.Requested:
                    this.waitForRefreshCycle(certificateKey);
                    break;
                case Domain.CertificateState.Failed:
                case Domain.CertificateState.Revoked:
                    AppKitchen.UIHelper.Notifications.notify("Failed", "warning", { closeAfter: 3000 });
                    break;
            }
        }

        private waitForRefreshCycle(certificateKey: number) {
            setTimeout(() => {
                if (this.state !== RequestCertificateState.Timeout) {
                    this.loadAndCheckCertificateSubmission(certificateKey);
                }
            }, this.refreshCycle);
        }

        private downloadCertificateFile(certificateKey: number) {
            new McEc.Data.Provider.CertificateFileProvider().load({ CertificateKey: certificateKey })
                .then(certificateFile => this.certificateFileDownloaded(certificateKey, certificateFile))
                .fail(message => {
                    this.changeState(RequestCertificateState.ErrorCertificateDownloaded);
                    AppKitchen.UIHelper.Notifications.notify(message, "warning");
                });
        }

        private certificateFileDownloaded(certificateKey: number, certificateFile: string) {
            if (this.state !== RequestCertificateState.Timeout) {
                this.changeState(RequestCertificateState.CertificateDownloaded);
                this.certificateKey = certificateKey;
                this.certificateFile = certificateFile;
                if (this.stateChanged) {
                    this.stateChanged(RequestCertificateState.Success);
                }
            }
        }
    }
}